import { z } from 'zod';

/**
 * Shape of the `segmentation_inputs` JSONB column on `segmentation_runs`.
 * Captures the user-facing choices that drove a segmentation run so they
 * can be displayed / compared after the originating playground session is gone.
 */

export const CategoryWeightSchema = z.object({
  /** Numeric importance weight assigned by the objective engine. */
  weight: z.number(),
  /** AI-generated explanation for why this weight was chosen. */
  reasoning: z.string(),
});
export type CategoryWeight = z.infer<typeof CategoryWeightSchema>;

export const SegmentationBrandFocusSchema = z.object({
  /** Whether the user selected specific brands (true) or proceeded with the entire market (false). */
  brandMode: z.boolean(),
  /** Molecules selected as the focal portfolio. Empty when brandMode is false. */
  focalMolecules: z.array(z.string()),
});
export type SegmentationBrandFocus = z.infer<typeof SegmentationBrandFocusSchema>;

export const SegmentationInputsSchema = z.object({
  /** Strategy key selected from the dropdown (e.g. "defend_share"), or null for NL-only objectives. */
  strategy: z.string().nullable(),
  /** Free-text objective entered or applied after qual refinement. */
  objectiveText: z.string().nullable(),
  /** Resolved category → weight map produced by the objective engine. */
  categoryWeights: z.record(z.string(), CategoryWeightSchema).nullable(),
  /** Market-research influence mode. */
  qualMode: z.enum(['data_driven', 'qual_informed', 'qual_primary']).nullable(),
  /** How personas were derived when market research is active. */
  personaBasis: z.enum(['research_segments', 'theme_derived']).nullable(),
  /** Brand focus configuration from the Configure panel. */
  brandFocus: SegmentationBrandFocusSchema.nullable(),
});
export type SegmentationInputs = z.infer<typeof SegmentationInputsSchema>;
